import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { PurchaseOrderView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';
import { useState } from 'react';

import { actOnPurchaseOrder, purchaseOrderQuery, type OrderAction } from '../api/procurement.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { LoadFailed, OfflineNote, ReasonDialog } from '../procurement/common.tsx';
import {
  documentLabel,
  documentTag,
  orderStateWords,
  RECEIPT_LABELS,
} from '../procurement/format.ts';
import { formatCents, formatInPacks, formatQuantity } from '../stock/format.ts';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

// One LPO (docs/plans/phase2-procurement.md §5.5): what was ordered, received and invoiced,
// and the actions its state allows.

const route = getRouteApi('/_app/procurement/orders/$orderId');

const ACTION_WORDS: Record<OrderAction, { title: string; confirm: string; toast: string }> = {
  approve: { title: 'Approve', confirm: 'Approve LPO', toast: 'LPO approved' },
  reject: { title: 'Reject', confirm: 'Reject LPO', toast: 'LPO rejected' },
  cancel: { title: 'Cancel', confirm: 'Cancel LPO', toast: 'LPO cancelled' },
  close: { title: 'Close', confirm: 'Close LPO', toast: 'LPO closed' },
};

export function PurchaseOrderPage() {
  const { orderId } = route.useParams();
  const order = useQuery(purchaseOrderQuery(orderId));
  if (order.isPending) return <LoadingBlock label="Loading the LPO" rows={5} />;
  if (order.isError)
    return (
      <LoadFailed
        error={order.error}
        fallback="The LPO couldn't be loaded."
        onRetry={() => void order.refetch()}
      />
    );
  return <OrderDetail order={order.data} />;
}

function OrderActions({ order }: { order: PurchaseOrderView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const [acting, setActing] = useState<OrderAction | null>(null);
  const act = useMutation({
    mutationFn: ({ action, reason }: { action: OrderAction; reason: string }) =>
      actOnPurchaseOrder(order.id, action, reason),
    onSuccess: async (result, { action }) => {
      queryClient.setQueryData(purchaseOrderQuery(order.id).queryKey, result);
      await queryClient.invalidateQueries({ queryKey: ['procurement', 'orders'] });
      toasts.show({ title: ACTION_WORDS[action].toast, description: result.number });
      setActing(null);
    },
  });
  if (session === null) return null;
  const anyReceived = order.lines.some((l) => l.received > 0);
  const mayOrder = can(session, 'procurement.order');
  const mine = session.user.id === order.submittedBy.id;
  const open = order.status === 'approved';
  return (
    <>
      <div className={styles.actions}>
        {order.canDecide ? (
          <>
            <Button
              variant="primary"
              isDisabled={!online}
              onPress={() => {
                setActing('approve');
              }}
            >
              Approve
            </Button>
            <Button
              isDisabled={!online}
              onPress={() => {
                setActing('reject');
              }}
            >
              Reject
            </Button>
          </>
        ) : null}
        {order.status === 'awaiting_approval' && mine ? (
          <span className={styles.meta}>
            Another person with approval rights must approve this.
          </span>
        ) : null}
        {order.status === 'approved' || order.status === 'closed' ? (
          <Link to="/procurement/orders/$orderId/print" params={{ orderId: order.id }}>
            Print LPO
          </Link>
        ) : null}
        {open && can(session, 'stock.receive') ? (
          <Link
            to="/stores/$storeId/receive"
            params={{ storeId: order.store.id }}
            search={{ orderId: order.id }}
          >
            Receive goods
          </Link>
        ) : null}
        {(open || order.status === 'closed') && can(session, 'procurement.invoice') ? (
          <Link to="/procurement/invoices/new" search={{ orderId: order.id }}>
            Record supplier invoice
          </Link>
        ) : null}
        {mayOrder && (order.status === 'awaiting_approval' || open) && !anyReceived ? (
          <Button
            variant="quiet"
            isDisabled={!online}
            onPress={() => {
              setActing('cancel');
            }}
          >
            Cancel LPO
          </Button>
        ) : null}
        {mayOrder && open && anyReceived ? (
          <Button
            variant="quiet"
            isDisabled={!online}
            onPress={() => {
              setActing('close');
            }}
          >
            Close LPO
          </Button>
        ) : null}
      </div>
      <OfflineNote what="Changing an LPO" />
      {acting === null ? null : (
        <ReasonDialog
          title={`${ACTION_WORDS[acting].title} ${order.number}`}
          confirmLabel={ACTION_WORDS[acting].confirm}
          isPending={act.isPending}
          error={act.error}
          onConfirm={(reason) => {
            act.mutate({ action: acting, reason });
          }}
          onClose={() => {
            setActing(null);
            act.reset();
          }}
        >
          {acting === 'close' ? (
            <p>Nothing more will be received against this LPO. Say why the rest isn't coming.</p>
          ) : null}
        </ReasonDialog>
      )}
    </>
  );
}

function OrderDetail({ order }: { order: PurchaseOrderView }) {
  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/procurement">Back to LPOs</Link>
      </p>
      <PageHeader title={`LPO ${order.number}`} />
      <Section
        title={order.supplier.name}
        actions={
          <Tag state={documentTag(order.status)}>
            {orderStateWords(order.status, order.receiptStatus)}
          </Tag>
        }
      >
        <dl className={styles.facts}>
          <dt>LPO</dt>
          <dd>
            <span className="wodi-id">{order.number}</span>
          </dd>
          <dt>Deliver to</dt>
          <dd>{order.store.name}</dd>
          {order.purchaseRequisition === null ? null : (
            <>
              <dt>Requisition</dt>
              <dd>
                <Link
                  to="/procurement/requisitions/$requisitionId"
                  params={{ requisitionId: order.purchaseRequisition.id }}
                >
                  <span className="wodi-id">{order.purchaseRequisition.number}</span>
                </Link>
              </dd>
            </>
          )}
          {order.expectedOn === null ? null : (
            <>
              <dt>Expected on</dt>
              <dd>{formatIsoDate(order.expectedOn)}</dd>
            </>
          )}
          <dt>Prepared by</dt>
          <dd>
            {order.submittedBy.name}, {formatInstant(order.submittedAt)}
          </dd>
          {order.decidedBy === null ? null : (
            <>
              <dt>{order.status === 'rejected' ? 'Rejected by' : 'Approved by'}</dt>
              <dd>
                {order.decidedBy.name}, {formatInstant(order.decidedAt ?? order.submittedAt)}
                {order.decisionReason === null ? '' : `: ${order.decisionReason}`}
              </dd>
            </>
          )}
          {order.closeReason === null ? null : (
            <>
              <dt>{order.status === 'cancelled' ? 'Cancelled' : 'Closed'}</dt>
              <dd>
                {order.closedBy?.name ?? ''}: {order.closeReason}
              </dd>
            </>
          )}
          <dt>Total</dt>
          <dd className={styles.numbers}>{formatCents(order.total.cents)}</dd>
        </dl>
        <OrderActions order={order} />
      </Section>
      <Section title="Lines">
        <TableScroll>
          <Table flat aria-label={`Lines of ${order.number}`}>
            <TableHeader>
              <Column isRowHeader>Item</Column>
              <Column numeric>Ordered</Column>
              <Column numeric>Price per pack</Column>
              <Column numeric>Line total</Column>
              <Column numeric>Received</Column>
              <Column numeric>Outstanding</Column>
              <Column numeric>Invoiced</Column>
              <Column>State</Column>
            </TableHeader>
            <TableBody items={order.lines}>
              {(l) => {
                const pack = { display: l.packUnitDisplay, baseUnitsPerPack: l.baseUnitsPerPack };
                return (
                  <Row id={l.id}>
                    <Cell>{l.itemName}</Cell>
                    <Cell numeric>{formatInPacks(l.quantity, l.baseUnitDisplay, pack)}</Cell>
                    <Cell numeric>{formatCents(l.packPrice.cents)}</Cell>
                    <Cell numeric>{formatCents(l.lineTotal.cents)}</Cell>
                    <Cell numeric>{formatQuantity(l.received, l.baseUnitDisplay)}</Cell>
                    <Cell numeric>{formatQuantity(l.outstanding, l.baseUnitDisplay)}</Cell>
                    <Cell numeric>{formatQuantity(l.invoiced, l.baseUnitDisplay)}</Cell>
                    <Cell>
                      <Tag state={l.receiptStatus === 'received' ? 'done' : 'pending'}>
                        {RECEIPT_LABELS[l.receiptStatus]}
                      </Tag>
                    </Cell>
                  </Row>
                );
              }}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
      <Section title="Deliveries">
        {order.receipts.length === 0 ? (
          <p className={styles.meta}>Nothing received against this LPO yet.</p>
        ) : (
          <ul>
            {order.receipts.map((r) => (
              <li key={r.id}>
                <Link
                  to="/stores/$storeId/receipts/$grnId"
                  params={{ storeId: r.storeId, grnId: r.id }}
                >
                  <span className="wodi-id">{r.number}</span>
                </Link>{' '}
                {formatInstant(r.receivedAt)}
                {r.status === 'draft' ? ', draft' : ''}
                {r.reversed ? ', reversed' : ''}
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Supplier invoices">
        {order.invoices.length === 0 ? (
          <p className={styles.meta}>No supplier invoices recorded against this LPO yet.</p>
        ) : (
          <ul>
            {order.invoices.map((i) => (
              <li key={i.id}>
                <Link to="/procurement/invoices/$invoiceId" params={{ invoiceId: i.id }}>
                  <span className="wodi-id">{i.number}</span>
                </Link>{' '}
                (their number <span className="wodi-id">{i.supplierReference}</span>),{' '}
                {formatCents(i.total.cents)}, {documentLabel(i.status).toLowerCase()}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
